<template>
    <span class="history-time">{{ formattedDateTime ?? '—' }}</span>
    <span class="history-event">
        <b class="history-action">{{ actionLabel }}</b>
        <span v-for="(detail, index) in lineDetails" :key="index" class="history-line">{{ detail }}</span>
        <span v-if="authorName" class="history-author">{{ authorName }}</span>
    </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface Props {
    record: any
    previousRecord?: any
}

const { record, previousRecord } = defineProps<Props>()

const normalizedAction = computed(() => {
    if (record?.action) return String(record.action).toUpperCase()
    if (record?.history_type === '+') return 'CREATED'
    return null
})

const actionLabels: Record<string, string> = {
    CREATED: 'Смена создана',
    UPDATED: 'Смена изменена',
    DELETED: 'Смена удалена',
    CHECKIN: 'Отметка на приход',
    CHECKOUT: 'Отметка на уход',
    FACT_UPDATED: 'Фактическое время изменено',
}

const actionLabel = computed(() => {
    const action = normalizedAction.value
    if (action && actionLabels[action]) return actionLabels[action]

    return record?.message || action || 'Изменение смены'
})

const createdAt = computed(() => record?.created_at || record?.history_date || null)

const formattedDateTime = computed(() => {
    if (!createdAt.value) return null

    return dayjs.utc(createdAt.value).local().format('DD.MM, HH:mm')
})

const messageText = computed(() => record?.message || null)
const authorName = computed(() => record?.user_name || null)

const formatTime = (value: string | null | undefined) => {
    if (!value) return null

    return dayjs(value).format('HH:mm')
}

const valueOrDash = (value: string | null | undefined) => value || '—'

const formatTimeOrDash = (value: string | null | undefined) => {
    const formatted = formatTime(value)
    return valueOrDash(formatted)
}

const isSameTime = (left: string | null | undefined, right: string | null | undefined) => {
    const leftTime = formatTime(left)
    const rightTime = formatTime(right)
    if (!leftTime && !rightTime) return true
    return leftTime === rightTime
}

const formatFactFallback = (source: any) => {
    const start = formatTime(source?.fact_work_start_at)
    const end = formatTime(source?.fact_work_end_at)

    if (start && end) return `Факт: ${start} — ${end}`
    if (start) return `Факт прихода: ${start}`
    if (end) return `Факт ухода: ${end}`
    return null
}

const getPositionCompareValue = (source: any) => source?.position?.id || source?.position?.name || null
const getPositionDisplayValue = (source: any) => source?.position?.name || source?.position?.id || null

const formatOvertime = (value: any) => value === true ? 'Да' : 'Нет'

const getComparedFieldDetails = (
    current: any,
    previous: any,
    options: { includeFactFields?: boolean; includeCommonFields?: boolean } = {},
) => {
    if (!previous) return []
    const { includeFactFields = false, includeCommonFields = true } = options

    const details: string[] = []

    if (!isSameTime(current?.plan_work_start_at, previous?.plan_work_start_at)) {
        details.push(`План начало: ${formatTimeOrDash(previous?.plan_work_start_at)} → ${formatTimeOrDash(current?.plan_work_start_at)}`)
    }

    if (!isSameTime(current?.plan_work_end_at, previous?.plan_work_end_at)) {
        details.push(`План окончание: ${formatTimeOrDash(previous?.plan_work_end_at)} → ${formatTimeOrDash(current?.plan_work_end_at)}`)
    }

    if (includeFactFields) {
        if (!isSameTime(current?.fact_work_start_at, previous?.fact_work_start_at)) {
            details.push(`Факт приход: ${formatTimeOrDash(previous?.fact_work_start_at)} → ${formatTimeOrDash(current?.fact_work_start_at)}`)
        }

        if (!isSameTime(current?.fact_work_end_at, previous?.fact_work_end_at)) {
            details.push(`Факт уход: ${formatTimeOrDash(previous?.fact_work_end_at)} → ${formatTimeOrDash(current?.fact_work_end_at)}`)
        }
    }

    if (includeCommonFields) {
        const currentDayType = current?.day_type_code || null
        const previousDayType = previous?.day_type_code || null
        if (currentDayType !== previousDayType) {
            details.push(`Тип дня: ${valueOrDash(previousDayType || undefined)} → ${valueOrDash(currentDayType || undefined)}`)
        }

        const currentPositionCompare = getPositionCompareValue(current)
        const previousPositionCompare = getPositionCompareValue(previous)
        if (currentPositionCompare !== previousPositionCompare) {
            details.push(`Должность: ${valueOrDash(getPositionDisplayValue(previous) || undefined)} → ${valueOrDash(getPositionDisplayValue(current) || undefined)}`)
        }

        const currentOvertime = current?.is_overtime
        const previousOvertime = previous?.is_overtime
        if (currentOvertime !== previousOvertime && currentOvertime !== undefined && previousOvertime !== undefined) {
            details.push(`Доп. смена: ${formatOvertime(previousOvertime)} → ${formatOvertime(currentOvertime)}`)
        }
    }

    return details
}

const getUpdatedDetails = () => getComparedFieldDetails(record, previousRecord, { includeFactFields: false, includeCommonFields: true })
const getFactUpdatedDetails = () => getComparedFieldDetails(record, previousRecord, { includeFactFields: true, includeCommonFields: false })

const eventDetails = computed(() => {
    const action = normalizedAction.value
    if (!action) return []

    if (action === 'CHECKIN') {
        const start = formatTime(record?.fact_work_start_at)
        return start ? [`Факт прихода: ${start}`] : []
    }

    if (action === 'CHECKOUT') {
        const end = formatTime(record?.fact_work_end_at)
        return end ? [`Факт ухода: ${end}`] : []
    }

    if (action === 'FACT_UPDATED') {
        const compared = getFactUpdatedDetails()
        if (compared.length) return compared

        const fact = formatFactFallback(record)
        return fact ? [fact] : []
    }

    if (action === 'UPDATED') {
        return getUpdatedDetails()
    }

    return []
})

// Каждая деталь — отдельной строкой: сообщение backend (если не стало подписью) и прежние «было → стало».
const lineDetails = computed(() => [
    ...(messageText.value && messageText.value !== actionLabel.value ? [messageText.value] : []),
    ...eventDetails.value,
])
</script>

<style scoped>
.history-time {
    color: var(--color-text-muted);
    font-variant-numeric: tabular-nums;
}

.history-event {
    min-width: 0;
    overflow-wrap: anywhere;
}

.history-action {
    display: block;
    font-weight: 600;
}

.history-line {
    display: block;
}

.history-author {
    display: block;
    color: var(--color-text-muted);
}
</style>
